import { useMemo } from 'react';
import { useAuth } from '@leagueplatform/auth-utils';
import {
  useAuthoringEnvironments,
  useConfigApp,
} from '@web-config-app/core-react';
import { isAdminUser } from '@web-config-app/core';
import { isProduction } from '@leagueplatform/app-environment';
import { useAccessibleTenants } from '../../hooks/use-accessible-tenants/use-accessible-tenants';
import { useTenantSelector } from '../../hooks/use-tenant-selector/use-tenant-selector';
import { getTenantConfig } from '../../utils/get-tenant-config';
import { AppTenant, type TenantId } from '../../types';
import { TenantSwitcher } from './tenant-switcher.component';

/**
 * Container component for the TenantSwitcher.
 * Handles fetching tenant data and managing tenant selection.
 * @returns TenantSwitcher component with appropriate props.
 */
export const TenantSwitcherContainer = () => {
  const { isAuthenticated } = useAuth();
  const { tenantId, tenantLogo } = useConfigApp();
  const { baseAuthoringEnvironment, multiEnvironmentAuthoringEnabled } =
    useAuthoringEnvironments();
  const {
    tenantConfigMap,
    isLoading: isDiscoveryLoading,
    error: discoveryError,
  } = useAccessibleTenants(isAuthenticated ?? false);

  // Inject the example tenant for League admins — it's not returned by
  // Auth0's accessible_tenants claim but is useful for internal testing.
  // Must be included in the config map so useTenantSelector can resolve it.
  // Only inject in non-production environments and only for admin users,
  // to avoid accidentally exposing it to non-admins or real users in prod.
  const enrichedConfigMap = useMemo(() => {
    if (!tenantConfigMap || !isAdminUser() || isProduction())
      return tenantConfigMap;
    if (tenantConfigMap[AppTenant.Example as TenantId]) return tenantConfigMap;

    const exampleConfig = getTenantConfig(AppTenant.Example);
    if (!exampleConfig) return tenantConfigMap;

    return {
      ...tenantConfigMap,
      [AppTenant.Example]: exampleConfig,
    };
  }, [tenantConfigMap]);

  const { selectTenant } = useTenantSelector(enrichedConfigMap);

  const accessibleTenants = enrichedConfigMap
    ? Object.entries(enrichedConfigMap)
        .map(([id, config]) => ({
          tenantId: id,
          tenantName: config.configApp.tenantName,
        }))
        .sort((a, b) => a.tenantName.localeCompare(b.tenantName))
    : [];

  return (
    <TenantSwitcher
      currentTenantId={tenantId}
      icon={tenantLogo}
      accessibleTenants={accessibleTenants}
      onSelectTenant={selectTenant}
      isDiscoveryLoading={isDiscoveryLoading}
      discoveryError={discoveryError}
      environmentLabel={
        !multiEnvironmentAuthoringEnabled
          ? baseAuthoringEnvironment.label
          : undefined
      }
    />
  );
};
